import { useMemo } from 'react';
import { AlarmClockPlus } from 'lucide-react';
import { useEntriesFollowReopenedPeriods, useMyReopenedPeriods } from '@/hooks/use-time-entries';
import { useNow } from '@/hooks/use-now';
import { reopenNoticeLines } from '../lib/reopen-window';
import { buildWeekDayKeys } from '../lib/week-days';

/**
 * Tells the viewer where a manager has reopened a closed month for them, from
 * which day, until when and what they may do there. The rows' pencil and trash follow the server's
 * flags, which are refetched whenever the live windows change. Renders nothing
 * without a live window that reopens a closed day of the shown week, and drops a line
 * on its own when the window expires; the rows follow the windows whichever
 * week is shown. [HW-963]
 */
export function ReopenedPeriodsNotice({ weekStart }: { weekStart: Date }) {
  const { data } = useMyReopenedPeriods();
  useEntriesFollowReopenedPeriods(data);
  const now = useNow(30_000);
  const weekDays = useMemo(() => buildWeekDayKeys(weekStart), [weekStart]);
  const lines = reopenNoticeLines(data ?? [], now, undefined, weekDays);
  if (lines.length === 0) return null;
  return (
    <div
      role="status"
      className="flex items-start gap-2.5 rounded-lg border border-hygge/30 bg-hygge/5 px-4 py-3 text-sm"
    >
      <AlarmClockPlus className="mt-0.5 h-4 w-4 shrink-0 text-hygge" />
      <div className="space-y-0.5">
        {lines.map((line) => (
          <p key={line.key} className="text-foreground">
            {line.text}
          </p>
        ))}
      </div>
    </div>
  );
}
